import { MarketplaceAvatar } from '@features/orders';
import { Button, Menu, cn } from '@skupreme/shared/ui';
import { getTabPermission } from '@contexts/auth';
import { PermissionTabKey } from '@utility/constants/permission';
import { MarketplacePriority } from '@utility/constants/catalog';

type ListingShop = {
  connected?: boolean;
  integrate?: boolean;
  status?: string;
  link?: string;
  shopName?: string;
  mf_enabled?: boolean;
  is_NARF?: boolean;
};

type ListingMarketplace = ListingShop & {
  type?: string;
  region_name?: string;
  shops?: ListingShop[];
};

type Entry = {
  id: string;
  key: string;
  marketplace: ListingMarketplace;
  listing: ListingShop;
};

/* Connected listings only, a shop at a time, in the usual marketplace order. */
function toEntries(
  marketplaces: Record<string, ListingMarketplace> | undefined,
): Entry[] {
  return Object.entries(marketplaces ?? {})
    .sort(
      ([a], [b]) =>
        (MarketplacePriority[a] ?? Number.MAX_SAFE_INTEGER) -
        (MarketplacePriority[b] ?? Number.MAX_SAFE_INTEGER),
    )
    .flatMap(([key, marketplace]) => {
      if (Array.isArray(marketplace.shops) && marketplace.shops.length) {
        return marketplace.shops
          .filter(shop => shop.connected)
          .map((shop, index) => ({
            id: `${key}-${index}`,
            key,
            marketplace,
            listing: shop,
          }));
      }

      return marketplace.connected
        ? [{ id: key, key, marketplace, listing: marketplace }]
        : [];
    });
}

function listingUrl(link?: string) {
  if (!link) return undefined;

  return link.includes('https://') ? link : `https://${link}`;
}

export type MarketplaceIconsProps = {
  /* The API's own shape is looser (passthrough shops); read as below. */
  marketplaces?: object | null;
  readOnly?: boolean;
  onToggleSync: (marketplaceKey: string, marketplace: unknown) => void;
};

/**
 * A listing's marketplaces as small logos. A paused one is greyed out, an
 * inactive one ringed in red; each opens the listing or pauses its sync.
 */
export function MarketplaceIcons({
  marketplaces,
  readOnly,
  onToggleSync,
}: MarketplaceIconsProps) {
  const { disabled, hidden } = getTabPermission({
    tab: PermissionTabKey.CatalogMarketplaces,
  });
  const entries = toEntries(
    marketplaces as Record<string, ListingMarketplace> | undefined,
  );

  /* No marketplace reads as an empty cell, not a dash. */
  if (!entries.length) {
    return null;
  }

  return (
    <div className="sk:flex sk:flex-wrap sk:gap-1.5">
      {entries.map(({ id, key, marketplace, listing }) => {
        const isInactive = (listing.status || '').toLowerCase() === 'inactive';
        const isPaused = !listing.integrate;
        const url = listingUrl(listing.link || marketplace.link);
        const name = listing.shopName || marketplace.type || key;
        const notes = [
          isInactive ? 'Inactive' : isPaused ? 'Sync paused' : 'Syncing',
          listing.mf_enabled && 'MF',
          listing.is_NARF && 'NARF',
        ]
          .filter(Boolean)
          .join(' · ');

        return (
          <Menu
            key={id}
            aria-label={`${name} listing`}
            trigger={
              <Button
                variant="ghost"
                shape="circle"
                aria-label={`${name}: ${notes}`}
                className="sk:size-auto sk:p-0"
              >
                <MarketplaceAvatar
                  marketplace={{
                    ...marketplace,
                    type: marketplace.type || key,
                  }}
                  isDimmed={isPaused}
                  className={cn(
                    'sk:rounded-lg',
                    isInactive && 'sk:ring-2 sk:ring-danger',
                  )}
                />
              </Button>
            }
            actions={[
              {
                id: 'open',
                label: 'Open listing',
                description: `${name} · ${notes}`,
                isDisabled: !url,
              },
              ...(hidden
                ? []
                : [
                    {
                      id: 'sync',
                      label: isPaused ? 'Resume sync' : 'Pause sync',
                      isDisabled: !!readOnly || !!disabled,
                    },
                  ]),
            ]}
            onAction={action => {
              if (action === 'open' && url) {
                window.open(url, '_blank', 'noopener,noreferrer');
              } else if (action === 'sync') {
                onToggleSync(key, marketplace);
              }
            }}
          />
        );
      })}
    </div>
  );
}
